/**
 * Personnel Section - Business Value Calculator
 * Sprint 7: Day 1
 *
 * 8-role table with auto-calculation:
 * Total = FTE × (Annual Salary / 12) × Duration (months)
 */

import React from 'react';

export interface PersonnelRole {
  id: string;
  name: string;
  fte: number;
  annualSalary: number;
  durationMonths: number;
  totalCost: number;
}

interface PersonnelSectionProps {
  roles: PersonnelRole[];
  onRoleUpdate: (roleId: string, field: keyof PersonnelRole, value: number) => void;
}

export const PersonnelSection: React.FC<PersonnelSectionProps> = ({
  roles,
  onRoleUpdate
}) => {
  // Handle input change with validation
  const handleInputChange = (
    roleId: string,
    field: keyof PersonnelRole,
    value: string
  ) => {
    const numericValue = parseFloat(value) || 0;

    // Validate ranges
    if (field === 'fte' && (numericValue < 0 || numericValue > 5)) return;
    if (field === 'annualSalary' && (numericValue < 0 || numericValue > 500000)) return;
    if (field === 'durationMonths' && (numericValue < 0 || numericValue > 36)) return;

    onRoleUpdate(roleId, field, numericValue);
  };

  return (
    <div className="personnel-section">
      <div className="section-help-text">
        <strong>Personnel Costs:</strong> Configure your implementation team.
        Total cost = FTE × (Annual Salary / 12) × Duration months.
        Typical FAIR implementation requires 2-6 FTEs over 6-12 months.
      </div>

      <table className="personnel-table">
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Role</th>
            <th style={{ textAlign: 'right', width: '120px' }}>FTE</th>
            <th style={{ textAlign: 'right', width: '140px' }}>Annual Salary</th>
            <th style={{ textAlign: 'right', width: '120px' }}>Duration (mo)</th>
            <th style={{ textAlign: 'right', width: '140px' }}>Total Cost</th>
          </tr>
        </thead>
        <tbody>
          {roles.map((role) => (
            <tr key={role.id}>
              <td>
                <strong>{role.name}</strong>
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  value={role.fte}
                  onChange={(e) => handleInputChange(role.id, 'fte', e.target.value)}
                  aria-label={`${role.name} FTE`}
                />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  max="500000"
                  step="1000"
                  value={role.annualSalary}
                  onChange={(e) => handleInputChange(role.id, 'annualSalary', e.target.value)}
                  aria-label={`${role.name} annual salary`}
                />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  max="36"
                  step="1"
                  value={role.durationMonths}
                  onChange={(e) => handleInputChange(role.id, 'durationMonths', e.target.value)}
                  aria-label={`${role.name} duration in months`}
                />
              </td>
              <td style={{ textAlign: 'right', fontWeight: 600, color: '#0094ab' }}>
                ${role.totalCost.toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #2d3748' }}>
            <td colSpan={4} style={{ textAlign: 'right', fontWeight: 700, padding: '1rem' }}>
              Total Personnel Costs:
            </td>
            <td style={{ textAlign: 'right', fontWeight: 700, fontSize: '1.125rem', color: '#0094ab', padding: '1rem' }}>
              ${roles.reduce((sum, r) => sum + r.totalCost, 0).toLocaleString()}
            </td>
          </tr>
        </tfoot>
      </table>

      <style>{`
        .section-help-text {
          background: rgba(0, 148, 171, 0.05);
          border-left: 4px solid #0094ab;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          font-size: 0.875rem;
          color: #4a5568;
          line-height: 1.6;
        }

        .personnel-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 1rem;
        }

        .personnel-table thead th {
          padding: 0.75rem 1rem;
          font-size: 0.75rem;
          font-weight: 700;
          color: #718096;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          background: #f7fafc;
          border-bottom: 2px solid #e2e8f0;
        }

        .personnel-table tbody td {
          padding: 1rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .personnel-table tbody tr:hover {
          background: #f7fafc;
        }

        .personnel-table input {
          width: 100%;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          text-align: right;
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .personnel-table input:focus {
          outline: none;
          border-color: #0094ab;
          box-shadow: 0 0 0 3px rgba(0, 148, 171, 0.1);
        }

        .personnel-table input:hover {
          border-color: #cbd5e0;
        }
      `}</style>
    </div>
  );
};

// Default roles based on Pistoia Alliance calculator
export const DEFAULT_PERSONNEL_ROLES: PersonnelRole[] = [
  {
    id: 'data_architect',
    name: 'Data Architect',
    fte: 0.5,
    annualSalary: 140000,
    durationMonths: 12,
    totalCost: 70000
  },
  {
    id: 'data_engineer',
    name: 'Data Engineer',
    fte: 1.0,
    annualSalary: 120000,
    durationMonths: 12,
    totalCost: 120000
  },
  {
    id: 'project_manager',
    name: 'Project Manager',
    fte: 0.5,
    annualSalary: 130000,
    durationMonths: 12,
    totalCost: 65000
  },
  {
    id: 'business_analyst',
    name: 'Business Analyst',
    fte: 0.5,
    annualSalary: 110000,
    durationMonths: 9,
    totalCost: 41250
  },
  {
    id: 'qa_specialist',
    name: 'QA Specialist',
    fte: 0.5,
    annualSalary: 100000,
    durationMonths: 6,
    totalCost: 25000
  },
  {
    id: 'devops_engineer',
    name: 'DevOps Engineer',
    fte: 0.3,
    annualSalary: 125000,
    durationMonths: 6,
    totalCost: 18750
  },
  {
    id: 'metadata_specialist',
    name: 'Metadata Specialist',
    fte: 0.5,
    annualSalary: 95000,
    durationMonths: 12,
    totalCost: 47500
  },
  {
    id: 'training_coordinator',
    name: 'Training Coordinator',
    fte: 0.2,
    annualSalary: 85000,
    durationMonths: 3,
    totalCost: 4250
  }
];

export default PersonnelSection;
